import Project, { UpdateProject } from '@/app/models/project';
import { useGetBuildingsQuery } from '@/app/services/buildingsApi';
import AmountField from '@/components/common/amountField';
import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

type RowFormats = string | number | boolean;

interface Column {
  id: 'code' | 'name' | 'building' | 'shareable' | 'goal';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center';
  format?: (value: RowFormats) => JSX.Element;
}

type RowData = {
  id: number;
  code: string;
  name: string;
  building: number;
  shareable: boolean;
  goal: number;
};

const columns: readonly Column[] = [
  {
    id: 'code',
    label: 'common:code',
    minWidth: 80,
  },
  { id: 'name', label: 'project.name', minWidth: 130 },
  { id: 'building', label: 'common:building', minWidth: 190 },
  { id: 'shareable', label: 'common:sharing.type', minWidth: 130 },
  { id: 'goal', label: 'Valor', minWidth: 70, align: 'center' },
];

const createRows = (content: Array<Project>) =>
  content.map(
    (project: Project): RowData =>
      ({
        id: project.id,
        code: project.project.code,
        name: project.project.name,
        building: project.building?.id,
        buildingAcronym: project.building?.acronym || '',
        shareable: project.sharable,
        goal: project.goal,
      } as RowData),
  );

interface ProjectTableRowProps {
  row: RowData;
  onClickRow: (clickedRow: RowData) => void;
  onDelete: (row: RowData) => void;
  selectedPeriod: string;
  map: Map<string, Map<number, UpdateProject>>;
  setMap: (map: Map<string, Map<number, UpdateProject>>) => void;
}

function ProjectTableRow(props: ProjectTableRowProps) {
  const { row, onClickRow: handleOnClickRow, onDelete: handleOnDelete, selectedPeriod, map, setMap } = props;
  const { data: buildings } = useGetBuildingsQuery({ searchTerm: '', active: true });
  const { t } = useTranslation(['expense', 'common']);
  const periodMap = map?.get(selectedPeriod);
  const currentProject = periodMap?.has(row.id)
    ? periodMap.get(row.id)
    : {
        id: row.id,
        shareable: row.shareable,
        goal: row.goal,
        buildingId: row.building,
        projectCode: row.code,
        period: selectedPeriod,
      };
  const [componentLoaded, setComponentLoaded] = useState(false);

  useEffect(() => {
    setComponentLoaded(true);
  }, []);

  return (
    <TableRow
      hover
      role="checkbox"
      tabIndex={-1}
      key={row.id}
      style={{ minHeight: '40px', height: '40px', maxHeight: '40px', cursor: 'pointer' }}
    >
      {columns.map((column) => {
        const value = row[column.id];
        const key = `${column.id}-${row.id}`;
        let formattedValue = column.format ? column.format(value) : value;
        if (column.id === 'building') {
          formattedValue = (
            <Select
              displayEmpty
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              sx={{ minWidth: 180, fontSize: '13px' }}
              value={currentProject?.buildingId || ''}
              variant="standard"
              onChange={(e) => {
                if (componentLoaded) {
                  if (currentProject) {
                    currentProject.buildingId = e.target.value as number;
                    if (periodMap) {
                      periodMap.set(row.id, currentProject);
                      map.set(selectedPeriod, periodMap);
                      setMap(new Map(map));
                    }
                  }
                }
              }}
            >
              <MenuItem key={-1} value="">
                <em>--- Nenhum ---</em>
              </MenuItem>
              {buildings?.map((building) => (
                <MenuItem key={building.building.id} value={building.building.id}>
                  {`${building.building.name} (${building.building.acronym})`}
                </MenuItem>
              ))}
            </Select>
          );
        }

        if (column.id === 'shareable') {
          formattedValue = (
            <Select
              displayEmpty
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              sx={{ minWidth: 180, fontSize: '13px' }}
              value={currentProject?.shareable ? 'rateavel' : 'naoRateavel'}
              variant="standard"
              onChange={(e) => {
                if (componentLoaded) {
                  if (currentProject) {
                    currentProject.shareable = e.target.value === 'rateavel';
                    if (periodMap) {
                      periodMap.set(row.id, currentProject);
                      map.set(selectedPeriod, periodMap);
                      setMap(new Map(map));
                    }
                  }
                }
              }}
            >
              <MenuItem value="rateavel">Rateável</MenuItem>
              <MenuItem value="naoRateavel">Não Rateável</MenuItem>
            </Select>
          );
        }

        if (column.id === 'goal') {
          formattedValue = (
            <AmountField
              value={currentProject?.goal as number}
              label=""
              disable={!currentProject?.shareable}
              sx={{ width: 130, margin: 0 }}
              onChange={(e) => {
                if (componentLoaded) {
                  if (currentProject) {
                    currentProject.goal = e;
                    if (periodMap) {
                      periodMap.set(row.id, currentProject);
                      map.set(selectedPeriod, periodMap);
                      setMap(new Map(map));
                    }
                  }
                }
              }}
            />
          );
        }
        return (
          <TableCell
            key={key}
            component="td"
            scope="row"
            align={column.align}
            onClick={() => handleOnClickRow(row)}
            sx={{ height: 5, fontSize: '13px', paddingY: 0, paddingX: 1 }}
          >
            {formattedValue}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

type ProjectsTableProps = {
  items: Array<Project>;
  onRowSelect: (item: Project) => void;
  onDelete: (item: Project) => void;
  rowsPerPage: number;
  page: number;
  handleChangePage: (event: any | null, page: number) => void;
  handleChangeRowsPerPage: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  itemCount: number;
  selectedPeriod: string;
  modifiedProjectsMap: Map<string, Map<number, UpdateProject>>;
  setModifiedProjectsMap: (map: Map<string, Map<number, UpdateProject>>) => void;
};

export default function ProjectsTable(props: ProjectsTableProps) {
  const { t } = useTranslation(['expense', 'common']);
  const [rows, setRows] = useState<Array<RowData>>([]);
  const calcTableHeight = () => window.innerHeight - 390;
  const [tableHeight, setTableHeight] = useState(calcTableHeight);
  const {
    items,
    onRowSelect,
    onDelete,
    rowsPerPage,
    page,
    handleChangePage,
    handleChangeRowsPerPage,
    itemCount,
    selectedPeriod,
    modifiedProjectsMap,
    setModifiedProjectsMap,
  } = props;

  useEffect(() => {
    setRows(createRows(items));
  }, [items]);

  const rowToPAccountingExpense = (row: RowData): Project => {
    const findItem = items.filter((item: Project) => item.id === row.id);
    if (findItem.length === 0) {
      toast.error(`${t('A despesa selecionado não existe mais...')}`, {
        position: toast.POSITION.TOP_RIGHT,
        delay: 1,
      });
    }
    return findItem[0];
  };

  const handleRowClick = (row: RowData) => {
    onRowSelect(rowToPAccountingExpense(row));
  };

  const handleDelete = (row: RowData) => {
    onDelete(rowToPAccountingExpense(row));
  };

  useEffect(() => {
    window.addEventListener('resize', () => setTableHeight(calcTableHeight), false);
  }, []);

  return (
    <>
      <TableContainer
        sx={{
          height: tableHeight,
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }} size="small" aria-label="Expense Table">
          <TableHead sx={{ fontWeight: 700 }}>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, fontWeight: 'bold', paddingLeft: 5 }}
                >
                  {t(column.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows &&
              rows.map((row: RowData) => (
                <ProjectTableRow
                  key={row.id}
                  row={row}
                  onClickRow={handleRowClick}
                  onDelete={handleDelete}
                  selectedPeriod={selectedPeriod}
                  map={modifiedProjectsMap}
                  setMap={setModifiedProjectsMap}
                />
              ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={8}>
                  {t('common:no.results.found')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter sx={{ width: '100%' }} />
        </Table>
      </TableContainer>
      <TablePagination
        sx={{ width: '100%' }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={itemCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('de')} ${count}`}
        labelRowsPerPage={t('items.per.page')}
      />
    </>
  );
}
